import React, { memo, useMemo } from "react";
import { getPublishedTime, sanitizeHtmlContent } from "@/helpers";
import { IMAGE_CDN, IMAGE_SHARE_CDN } from "@/constant/apiPath";
import type { BlogData, INewsResponse } from "@/models/news";
import { Avatar, Skeleton, Typography } from "antd/lib";
import classNames from "classnames";
import Link from "next/link";
import useTimeCounter from "@/hooks/useTimeCounter";
import logoBlog from "@/asset/images/logo-blog-klarda.png";
import { useLanguageContext } from "@/context/LanguageContext";

const BlogNews = ({
  news,
  isLoading,
  isHideDesc,
  imageClassName,
}: {
  news?: BlogData | INewsResponse;
  isLoading?: boolean;
  isHideDesc?: boolean;
  imageClassName?: string;
}) => {
  const isKlarda = news && "is_klarda" in news && news.is_klarda === false ? false : true;
  const title = useMemo(() => sanitizeHtmlContent(news?.title || news?.description || ""), [news]);
  const { language } = useLanguageContext();

  const description = useMemo(() => sanitizeHtmlContent(news?.description || news?.content || ""), [news]);

  const imageUrl = useMemo(() => {
    if (typeof news?.thumbnail === "object" && news?.thumbnail?.imageKey) {
      return `${IMAGE_SHARE_CDN}${news.thumbnail.imageKey}`;
    }
    return news?.thumbnail;
  }, [news]);

  const publishDate = useTimeCounter({ publishDate: getPublishedTime(news as BlogData) });

  if (isLoading) return <Skeleton paragraph={{ rows: 5 }} />;
  return (
    <div className="flex flex-col gap-4 mycard-hover p-1 rounded">
      <Link
        href={isKlarda ? `/${language}/news/blogs/${news?.link}` : `/${language}/news/${news?.id}`}
        passHref
        key={news?.id}
      >
        <img src={imageUrl as string} alt={title} className={classNames(`rounded w-full ${imageClassName ?? ""}`)} />
      </Link>
      <div className="flex flex-col gap-2 flex-1 justify-between">
        <div className="space-y-1">
          <Typography.Link
            title={title}
            href={isKlarda ? `/${language}/news/blogs/${news?.link}` : `/${language}/news/${news?.id}`}
          >
            <Typography.Title level={5} className="line-clamp-2 m-0">
              <div dangerouslySetInnerHTML={{ __html: title }} />
            </Typography.Title>
          </Typography.Link>
          {!isHideDesc && (
            <Typography.Text title={description} type="secondary" className="line-clamp-2">
              <div dangerouslySetInnerHTML={{ __html: description }} />
            </Typography.Text>
          )}
        </div>
        <Skeleton loading={!news} active title={false} paragraph={{ rows: 1, width: "100%" }}>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1 whitespace-nowrap w-7/12 flex-1">
              {news && "user_id" in news && news.user_id?.profile_information?.avatar ? (
                <Avatar src={`${IMAGE_CDN}${news.user_id.profile_information.avatar}`} size={16} />
              ) : news?.source_logo ? (
                <Avatar src={`${IMAGE_CDN}${news.source_logo}`} size={16} />
              ) : (
                <Avatar src={logoBlog.src} size={16} alt="Klarda Blog" />
              )}
              <Typography.Text className="flex-1" ellipsis>
                {(news && "user_id" in news ? news.user_id?.profile_information?.display_name : news?.creator) ??
                  "Klarda Blog"}
              </Typography.Text>
            </div>
            <div className="flex items-center justify-end w-5/12">
              <Typography.Text type="secondary" ellipsis>
                {publishDate}
              </Typography.Text>
            </div>
          </div>
        </Skeleton>
      </div>
    </div>
  );
};

export default memo(BlogNews);
